// Files on every Opportunity and Job (18 October 2026): the plans, photos, supplier and customer papers a piece of
// work collects, kept in the private `aiw-uploads` container at `record-files/<ref>/<uuid>.<ext>` and recorded in
// `crm.record_file`. One set of rules for the three places that need them: the reserve command (packages/commands),
// the chunked upload in the API, and the CRM's drop zone.
/** 100 MB, the agreed ceiling per file. The database check holds the same number. */
export const RECORD_FILE_MAX_BYTES = 104_857_600;
/** One chunk of the upload through the API (Azure block blob, staged then committed). The last chunk is shorter. */
export const RECORD_FILE_CHUNK_BYTES = 8 * 1024 * 1024;
export const RECORD_FILE_CATEGORIES = ["plans", "photos", "supplier", "customer", "other"];
export const RECORD_FILE_SOURCES = ["upload", "email", "whatsapp", "sms"];
export const RECORD_FILE_PREFIX = "record-files/";
/** Accepted content types and the extension each is kept under. */
const TYPES = {
    "application/pdf": "pdf",
    "image/jpeg": "jpg", "image/png": "png", "image/webp": "webp", "image/heic": "heic", "image/heif": "heif",
    "video/mp4": "mp4", "video/quicktime": "mov",
    "application/acad": "dwg", "image/vnd.dxf": "dxf",
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document": "docx",
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": "xlsx",
    "application/msword": "doc", "application/vnd.ms-excel": "xls",
    "text/csv": "csv",
};
/** What a browser or a mail client may call a drawing; the kept type is one name per format. */
const DWG_ALIASES = new Set(["image/vnd.dwg", "application/acad", "application/x-dwg", "application/x-acad", "application/autocad_dwg", "application/dwg", "image/x-dwg"]);
const DXF_ALIASES = new Set(["image/vnd.dxf", "application/dxf", "application/x-dxf", "image/x-dxf"]);
/** A browser often sends no type (or octet-stream) for these; the name's extension then decides. */
const BY_EXTENSION = {
    pdf: "application/pdf", jpg: "image/jpeg", jpeg: "image/jpeg", png: "image/png", webp: "image/webp", heic: "image/heic", heif: "image/heif",
    mp4: "video/mp4", mov: "video/quicktime", dwg: "application/acad", dxf: "image/vnd.dxf",
    docx: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    xlsx: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    doc: "application/msword", xls: "application/vnd.ms-excel", csv: "text/csv",
};
function extensionOf(name) {
    const m = /\.([a-z0-9]{1,5})$/i.exec(name.trim());
    return m ? m[1].toLowerCase() : "";
}
/**
 * The content type a file is kept under and its extension, or null when it is not accepted.
 * A drawing is recognised by its name (.dwg → application/acad, .dxf → image/vnd.dxf) whatever the browser called it;
 * any other type must be on the list, or be blank / octet-stream with a known extension.
 */
export function recordFileType(name, contentType) {
    const ext = extensionOf(name);
    const declared = contentType.trim().toLowerCase().split(";")[0].trim();
    const blank = declared === "" || declared === "application/octet-stream";
    if (ext === "dwg" && (blank || DWG_ALIASES.has(declared)))
        return { content_type: "application/acad", ext: "dwg" };
    if (ext === "dxf" && (blank || DXF_ALIASES.has(declared)))
        return { content_type: "image/vnd.dxf", ext: "dxf" };
    if (declared in TYPES && declared !== "application/acad" && declared !== "image/vnd.dxf")
        return { content_type: declared, ext: TYPES[declared] };
    if (blank && BY_EXTENSION[ext]) {
        const t = BY_EXTENSION[ext];
        return { content_type: t, ext: TYPES[t] };
    }
    return null;
}
/** The file picker's accept list, so the CRM offers what the server keeps. */
export const RECORD_FILE_ACCEPT = Object.keys(BY_EXTENSION).map((e) => `.${e}`).join(",");
/** How many chunks a file of `size` bytes is sent in. */
export function recordFileChunks(size, chunk = RECORD_FILE_CHUNK_BYTES) {
    return Math.max(1, Math.ceil(size / chunk));
}
/** The exact byte length chunk `n` (0-based) must have. */
export function recordFileChunkLength(size, n, chunk = RECORD_FILE_CHUNK_BYTES) {
    const count = recordFileChunks(size, chunk);
    if (!Number.isInteger(n) || n < 0 || n >= count)
        return -1;
    return n === count - 1 ? size - chunk * (count - 1) : chunk;
}
//# sourceMappingURL=record-file.js.map